import type { FC, MutableRefObject } from 'react'
import type { Photo } from '@/constants/moments'

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'

import {
    Bookmark,
    ChevronLeft,
    ChevronRight,
    Download,
    Heart,
    Share,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'

/* ── Grid + tile geometry ─────────────────────────────────────────────── */
const CELL_H_DESKTOP = 100
const CELL_H_MOBILE = 72
const CELL_GAP_DESKTOP = 12
const CELL_GAP_MOBILE = 8
const TILE_SCALE_DESKTOP = 3
const TILE_SCALE_MOBILE = 2.6

/* ── Gravity void radii (idle < hover < expanded) ─────────────────────── */
const VOID_RADIUS_IDLE_DESKTOP = 200
const VOID_RADIUS_HOVER_DESKTOP = 280
const VOID_RADIUS_EXPANDED_DESKTOP = 420
const VOID_RADIUS_IDLE_MOBILE = 130
const VOID_RADIUS_HOVER_MOBILE = 180
const VOID_RADIUS_EXPANDED_MOBILE = 280

/* ── Physics tuning ───────────────────────────────────────────────────── */
const VOID_LERP = 0.25
const PAN_LERP = 0.07
const RADIUS_LERP = 0.07
const SOFT_PUSH_RATIO = 1.3

/* Studio contact used by the Order print / Inquire actions. Replace with a
   real address (or wire to a form) when ready. */
const CONTACT_EMAIL = 'studio@moments.gallery'

const CELL_CLASS =
    'absolute left-0 top-0 select-none overflow-hidden rounded-[3px] pointer-events-none will-change-transform origin-center [backface-visibility:hidden]'

/* ── Deterministic helpers (no Math.random anywhere) ──────────────────── */
function hash(n: number): number {
    const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453
    return x - Math.floor(x)
}

function smoothstep(a: number, b: number, x: number): number {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
}

function rowDriftSpeed(rowIndex: number): number {
    const magnitude = 0.05 + hash(rowIndex * 41.3 + 17.1) * 0.18
    const sign = rowIndex % 2 === 0 ? -1 : 1
    return magnitude * sign
}

/* ── Types ────────────────────────────────────────────────────────────── */
interface Cell {
    index: number
    rowIndex: number
    baseX: number
    baseY: number
    width: number
    height: number
    photoIndex: number
    angle: number
    rotation: number
    scale: number
}

interface TileSize {
    width: number
    height: number
    rows: number
}

interface Dims {
    vw: number
    vh: number
    isMobile: boolean
}

interface RawItem {
    photoIndex: number
    width: number
    rawX: number
    colIdx: number
}

/* ── Build the wrapping tile of cells ─────────────────────────────────── */
function buildCells(dims: Dims, photos: Photo[]): { cells: Cell[]; tile: TileSize } {
    const { vw, vh, isMobile } = dims
    const cellH = isMobile ? CELL_H_MOBILE : CELL_H_DESKTOP
    const gap = isMobile ? CELL_GAP_MOBILE : CELL_GAP_DESKTOP
    const scale = isMobile ? TILE_SCALE_MOBILE : TILE_SCALE_DESKTOP
    const stepY = cellH + gap

    const targetTileW = vw * scale
    const targetTileH = vh * scale

    const rows = Math.ceil(targetTileH / stepY)
    const tileH = rows * stepY
    const firstRowY = -tileH / 2 + cellH / 2 + gap / 2

    /* Pass 1 — pick photos with anti-collision, record natural row widths. */
    const rawRows: RawItem[][] = []
    let maxNaturalWidth = 0
    let prevRow: RawItem[] | null = null

    for (let r = 0; r < rows; r++) {
        const rowItems: RawItem[] = []
        let rawX = 0
        let colIdx = 0

        while (rawX < targetTileW) {
            let candidate = Math.floor(hash(r * 17.3 + colIdx * 31.7 + 5) * photos.length)

            const leftIdx = rowItems.length > 0 ? rowItems[rowItems.length - 1].photoIndex : -1
            let aboveIdx = -1
            if (prevRow) {
                for (const prev of prevRow) {
                    if (rawX >= prev.rawX && rawX < prev.rawX + prev.width + gap) {
                        aboveIdx = prev.photoIndex
                        break
                    }
                }
            }

            let attempts = 0
            while ((candidate === leftIdx || candidate === aboveIdx) && attempts < photos.length) {
                candidate = (candidate + 1) % photos.length
                attempts++
            }

            const aspect = photos[candidate].aspect
            const width = cellH * aspect
            rowItems.push({ photoIndex: candidate, width, rawX, colIdx })
            rawX += width + gap
            colIdx++
        }

        rawRows.push(rowItems)
        prevRow = rowItems

        const rowNaturalWidth = rawX - gap
        if (rowNaturalWidth > maxNaturalWidth) maxNaturalWidth = rowNaturalWidth
    }

    /* Tile width = widest natural row; shorter rows widen their gaps to match,
       so the horizontal wrap seam is clean. */
    const tileW = maxNaturalWidth + gap

    /* Pass 2 — commit Cell objects, each row centred on world x = 0. */
    const cells: Cell[] = []
    let idx = 0

    for (let r = 0; r < rows; r++) {
        const rowItems = rawRows[r]
        if (rowItems.length === 0) continue

        const last = rowItems[rowItems.length - 1]
        const rowNaturalWidth = last.rawX + last.width
        const extra = tileW - rowNaturalWidth - gap
        const extraPerGap = rowItems.length > 1 ? extra / rowItems.length : 0

        let runningX = -tileW / 2 + gap / 2
        for (const item of rowItems) {
            const cellCenterX = runningX + item.width / 2
            cells.push({
                index: idx++,
                rowIndex: r,
                baseX: cellCenterX,
                baseY: r * stepY + firstRowY,
                width: item.width,
                height: cellH,
                photoIndex: item.photoIndex,
                angle: hash(r * 31 + item.colIdx * 17 + 7) * Math.PI * 2,
                rotation: (hash(r * 5 + item.colIdx * 11 + 3) - 0.5) * 3.6,
                scale: 1 + (hash(r * 7 + item.colIdx * 13 + 5) - 0.5) * 0.05
            })
            runningX += item.width + gap + extraPerGap
        }
    }

    return { cells, tile: { width: tileW, height: tileH, rows } }
}

/* ── The wall — memoised so featured/expanded changes never re-render it ─ */
interface WallProps {
    cells: Cell[]
    photos: Photo[]
    itemsRef: MutableRefObject<(HTMLDivElement | null)[]>
}

const Wall = memo(function Wall({ cells, photos, itemsRef }: WallProps) {
    return (
        <>
            {cells.map((cell, i) => {
                const photo = photos[cell.photoIndex]
                return (
                    <div
                        key={cell.index}
                        ref={(el) => {
                            itemsRef.current[i] = el
                        }}
                        className={CELL_CLASS}
                        style={{ width: cell.width, height: cell.height, opacity: 0 }}
                    >
                        <img
                            src={photo.thumbUrl}
                            alt=""
                            draggable={false}
                            decoding="async"
                            className="h-full w-full select-none object-cover"
                        />
                    </div>
                )
            })}
        </>
    )
})

/* ── The featured card (rest + expanded detail) ───────────────────────── */
interface FeaturedCardProps {
    photo: Photo
    expanded: boolean
    isMobile: boolean
    liked: boolean
    saved: boolean
    onOpen: () => void
    onClose: () => void
    onNext: () => void
    onPrev: () => void
    onToggleLike: () => void
    onToggleSave: () => void
    onShare: () => void
    onDownload: () => void
    onOrder: () => void
    onInquire: () => void
}

const FeaturedCard: FC<FeaturedCardProps> = ({
    photo,
    expanded,
    isMobile,
    liked,
    saved,
    onOpen,
    onClose,
    onNext,
    onPrev,
    onToggleLike,
    onToggleSave,
    onShare,
    onDownload,
    onOrder,
    onInquire
}) => {
    const restH = isMobile ? 220 : 280
    const restMaxW = isMobile ? 270 : 400
    const expH = isMobile ? 280 : 360
    const expMaxW = isMobile ? 300 : 480
    const expMinW = isMobile ? 260 : 340

    const h = expanded ? expH : restH
    const maxW = expanded ? expMaxW : restMaxW
    let w = h * photo.aspect
    let frameH = h
    if (w > maxW) {
        w = maxW
        frameH = maxW / photo.aspect
    } else if (expanded && w < expMinW) {
        w = expMinW
        frameH = expMinW / photo.aspect
    }

    const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
    const display = 'var(--font-playfair), Georgia, serif'

    const likeBtn = (
        <button
            type="button"
            onClick={(e) => {
                stop(e)
                onToggleLike()
            }}
            className="moments-icon-btn size-9"
            aria-label={liked ? 'Remove like' : 'Like'}
            aria-pressed={liked}
        >
            <Heart className="size-4" fill={liked ? 'currentColor' : 'none'} />
        </button>
    )
    const saveBtn = (
        <button
            type="button"
            onClick={(e) => {
                stop(e)
                onToggleSave()
            }}
            className="moments-icon-btn size-9"
            aria-label={saved ? 'Remove from saved' : 'Save'}
            aria-pressed={saved}
        >
            <Bookmark className="size-4" fill={saved ? 'currentColor' : 'none'} />
        </button>
    )
    const shareBtn = (
        <button
            type="button"
            onClick={(e) => {
                stop(e)
                onShare()
            }}
            className="moments-icon-btn size-9"
            aria-label="Share"
        >
            <Share className="size-4" />
        </button>
    )
    const downloadBtn = (
        <button
            type="button"
            onClick={(e) => {
                stop(e)
                onDownload()
            }}
            className="moments-icon-btn size-9"
            aria-label="View full image"
        >
            <Download className="size-4" />
        </button>
    )
    const closeBtn = (
        <button
            type="button"
            onClick={(e) => {
                stop(e)
                onClose()
            }}
            className="moments-icon-btn size-9"
            aria-label="Close"
        >
            <X className="size-4" />
        </button>
    )

    return (
        <div
            className={cn('pointer-events-auto absolute', !expanded && 'cursor-pointer')}
            style={{
                width: w,
                height: frameH,
                transform: 'translate(-50%, -50%)',
                transition:
                    'width 0.25s cubic-bezier(0.16, 1, 0.3, 1), height 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={onOpen}
            role={expanded ? undefined : 'button'}
            tabIndex={expanded ? undefined : 0}
            aria-label={expanded ? undefined : `Open ${photo.title}`}
            onKeyDown={
                expanded
                    ? undefined
                    : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onOpen()
                          }
                      }
            }
        >
            {expanded && (
                <div className="absolute bottom-full left-1/2 mb-4 w-max max-w-[80vw] -translate-x-1/2 text-center">
                    <div className="flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={(e) => {
                                stop(e)
                                onPrev()
                            }}
                            className="moments-icon-btn size-8"
                            aria-label="Previous photo"
                        >
                            <ChevronLeft className="size-4" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                stop(e)
                                onNext()
                            }}
                            className="moments-icon-btn size-8"
                            aria-label="Next photo"
                        >
                            <ChevronRight className="size-4" />
                        </button>
                    </div>
                    <div
                        className="mt-3 text-[10px] uppercase tracking-[0.24em]"
                        style={{ color: 'rgb(var(--foreground-rgb) / 0.6)' }}
                    >
                        {photo.category}
                    </div>
                    <div
                        className="mt-1"
                        style={{
                            fontFamily: display,
                            fontSize: isMobile ? 24 : 30,
                            lineHeight: 1.1,
                            color: 'var(--foreground)'
                        }}
                    >
                        {photo.title}
                    </div>
                </div>
            )}

            {expanded && !isMobile && (
                <div className="absolute right-full top-1/2 mr-4 flex -translate-y-1/2 flex-col gap-2">
                    {likeBtn}
                    {saveBtn}
                </div>
            )}

            {expanded && !isMobile && (
                <div className="absolute left-full top-1/2 ml-4 flex -translate-y-1/2 flex-col gap-2">
                    {shareBtn}
                    {downloadBtn}
                    {closeBtn}
                </div>
            )}

            <div
                className="relative h-full w-full overflow-hidden rounded-[6px] bg-black"
                style={{ boxShadow: '0 30px 70px -15px rgb(0 0 0 / 0.7)' }}
            >
                <img
                    src={photo.imageUrl}
                    alt={photo.title}
                    draggable={false}
                    className="h-full w-full object-cover"
                />
                {!expanded && (
                    <div
                        className="absolute inset-x-0 bottom-0 p-3"
                        style={{
                            background:
                                'linear-gradient(to top, rgb(0 0 0 / 0.85) 0%, rgb(0 0 0 / 0.4) 50%, transparent 100%)'
                        }}
                    >
                        <div className="text-[10px] uppercase tracking-[0.22em] text-white/75">
                            {photo.category}
                        </div>
                        <div className="truncate text-white" style={{ fontFamily: display, fontSize: 16 }}>
                            {photo.title}
                        </div>
                    </div>
                )}
            </div>

            {expanded && (
                <div
                    className="absolute left-1/2 top-full mt-4 -translate-x-1/2 text-center"
                    style={{ minWidth: isMobile ? 260 : 320, maxWidth: '90vw' }}
                >
                    <div
                        className="text-[11px] uppercase tracking-[0.24em]"
                        style={{ color: 'rgb(var(--foreground-rgb) / 0.6)' }}
                    >
                        {photo.location} · {photo.year}
                    </div>

                    {isMobile && (
                        <div className="mt-3 flex items-center justify-center gap-2">
                            {likeBtn}
                            {saveBtn}
                            {shareBtn}
                            {downloadBtn}
                            {closeBtn}
                        </div>
                    )}

                    {!isMobile && (
                        <p
                            className="mx-auto mt-3 text-[13px]"
                            style={{ color: 'rgb(var(--foreground-rgb) / 0.7)', maxWidth: 440, lineHeight: 1.6 }}
                        >
                            {photo.description}
                        </p>
                    )}

                    <div className="mt-4 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={(e) => {
                                stop(e)
                                onOrder()
                            }}
                            className="moments-btn-primary px-6 py-3 text-sm font-medium"
                        >
                            Order print
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                stop(e)
                                onInquire()
                            }}
                            className="moments-btn-secondary px-6 py-3 text-sm font-medium"
                        >
                            Inquire
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

/* ── The section ──────────────────────────────────────────────────────── */
interface MomentsProps {
    photos: Photo[]
}

export const Moments: FC<MomentsProps> = ({ photos = [] }) => {
    const [dims, setDims] = useState<Dims | null>(null)
    const [featuredIdx, setFeaturedIdx] = useState(0)
    const [expanded, setExpanded] = useState(false)
    const [liked, setLiked] = useState<Set<string>>(() => new Set())
    const [saved, setSaved] = useState<Set<string>>(() => new Set())

    const sectionRef = useRef<HTMLElement | null>(null)
    const anchorRef = useRef<HTMLDivElement | null>(null)
    const heroRef = useRef<HTMLDivElement | null>(null)
    const eyebrowRef = useRef<HTMLDivElement | null>(null)
    const titleRef = useRef<HTMLHeadingElement | null>(null)
    const subRef = useRef<HTMLParagraphElement | null>(null)
    const hintRef = useRef<HTMLDivElement | null>(null)
    const itemEls = useRef<(HTMLDivElement | null)[]>([])

    const physics = useRef({
        mouse: { x: 0, y: 0, has: false },
        panOff: { x: 0, y: 0 },
        panTarget: { x: 0, y: 0 },
        voidWorld: { x: 0, y: 0 },
        voidRadius: 0,
        targetRadius: 0,
        rowOffsets: [] as number[],
        hovering: true,
        pinned: false,
        expanded: false,
        featuredIdx: 0,
        hasInteracted: false
    })
    const intro = useRef({ entrance: 0, voidEmerge: 0, featuredEmerge: 0 })
    const introStarted = useRef(false)

    const built = useMemo(() => (dims && photos.length > 0 ? buildCells(dims, photos) : null), [dims, photos])

    const currentPhoto: Photo =
        built && built.cells.length > 0
            ? photos[built.cells[Math.min(featuredIdx, built.cells.length - 1)].photoIndex]
            : photos[0]

    /* Keep refs mirrored from state so the rAF loop reads fresh values. */
    useEffect(() => {
        physics.current.expanded = expanded
    }, [expanded])
    useEffect(() => {
        physics.current.featuredIdx = featuredIdx
    }, [featuredIdx])

    /* Viewport sizing with the mobile browser-chrome resize filter. */
    useEffect(() => {
        const update = () => {
            const vw = window.innerWidth
            const vh = window.innerHeight
            const isMobile = vw < 768
            setDims((prev) => {
                if (!prev) return { vw, vh, isMobile }
                if (prev.vw === vw && prev.isMobile === isMobile && Math.abs(prev.vh - vh) < 200) {
                    return prev
                }
                return { vw, vh, isMobile }
            })
        }
        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    /* ── Actions ─────────────────────────────────────────────────────── */
    const open = useCallback(() => {
        const p = physics.current
        if (p.expanded || !dims) return
        p.pinned = false
        p.panTarget.x = dims.vw / 2 - p.voidWorld.x
        p.panTarget.y = dims.vh / 2 - p.voidWorld.y
        p.expanded = true
        setExpanded(true)
    }, [dims])

    const close = useCallback(() => {
        const p = physics.current
        p.pinned = false
        p.expanded = false
        setExpanded(false)
    }, [])

    const navigate = useCallback(
        (dir: 1 | -1) => {
            if (!built || !dims) return
            const { cells, tile } = built
            if (cells.length === 0) return
            const p = physics.current
            const newIdx = (p.featuredIdx + dir + cells.length) % cells.length
            const nextCell = cells[newIdx]

            const driftedX = nextCell.baseX + (p.rowOffsets[nextCell.rowIndex] ?? 0)
            const baseScreenX0 = driftedX + p.panOff.x
            const baseScreenY0 = nextCell.baseY + p.panOff.y
            const kX = Math.round((dims.vw / 2 - baseScreenX0) / tile.width)
            const kY = Math.round((dims.vh / 2 - baseScreenY0) / tile.height)
            const nextWorldX = driftedX + kX * tile.width
            const nextWorldY = nextCell.baseY + kY * tile.height

            p.pinned = true
            p.panTarget.x = dims.vw / 2 - nextWorldX
            p.panTarget.y = dims.vh / 2 - nextWorldY
            p.featuredIdx = newIdx
            setFeaturedIdx(newIdx)
        },
        [built, dims]
    )

    const next = useCallback(() => navigate(1), [navigate])
    const prev = useCallback(() => navigate(-1), [navigate])

    const toggleLike = useCallback(() => {
        const id = currentPhoto.slug
        setLiked((prev) => {
            const set = new Set(prev)
            if (set.has(id)) set.delete(id)
            else set.add(id)
            return set
        })
    }, [currentPhoto.slug])

    const toggleSave = useCallback(() => {
        const id = currentPhoto.slug
        setSaved((prev) => {
            const set = new Set(prev)
            if (set.has(id)) set.delete(id)
            else set.add(id)
            return set
        })
    }, [currentPhoto.slug])

    const share = useCallback(async () => {
        const url = typeof window !== 'undefined' ? window.location.href : ''
        const text = `${currentPhoto.title} — ${currentPhoto.location}, ${currentPhoto.year}`
        try {
            if (navigator.share) {
                await navigator.share({ title: currentPhoto.title, text, url })
                return
            }
            await navigator.clipboard.writeText(`${text} ${url}`.trim())
        } catch {
            /* user dismissed the share/clipboard prompt — nothing to do */
        }
    }, [currentPhoto])

    const download = useCallback(() => {
        window.open(currentPhoto.imageUrl, '_blank', 'noopener,noreferrer')
    }, [currentPhoto])

    const order = useCallback(() => {
        const subject = encodeURIComponent(`Print order — ${currentPhoto.title}`)
        const body = encodeURIComponent(
            `I'd like to order a print of "${currentPhoto.title}" (${currentPhoto.location}, ${currentPhoto.year}).`
        )
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`
    }, [currentPhoto])

    const inquire = useCallback(() => {
        const subject = encodeURIComponent(`Inquiry — ${currentPhoto.title}`)
        const body = encodeURIComponent(`I have a question about "${currentPhoto.title}".`)
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`
    }, [currentPhoto])

    /* ── Pointer / touch / hover listeners ───────────────────────────── */
    useEffect(() => {
        const p = physics.current
        const fadeHint = () => {
            if (p.hasInteracted) return
            p.hasInteracted = true
            if (hintRef.current) gsap.to(hintRef.current, { opacity: 0, duration: 0.5, overwrite: true })
        }
        const onMove = (e: PointerEvent) => {
            p.mouse.x = e.clientX
            p.mouse.y = e.clientY
            p.mouse.has = true
            fadeHint()
        }
        const onTouch = (e: TouchEvent) => {
            if (e.touches.length === 0) return
            p.mouse.x = e.touches[0].clientX
            p.mouse.y = e.touches[0].clientY
            p.mouse.has = true
            fadeHint()
        }
        const onEnter = () => {
            p.hovering = true
        }
        const onLeave = () => {
            p.hovering = false
        }
        const section = sectionRef.current
        window.addEventListener('pointermove', onMove)
        window.addEventListener('touchmove', onTouch, { passive: true })
        if (section) {
            section.addEventListener('pointerenter', onEnter)
            section.addEventListener('pointerleave', onLeave)
        }
        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('touchmove', onTouch)
            if (section) {
                section.removeEventListener('pointerenter', onEnter)
                section.removeEventListener('pointerleave', onLeave)
            }
        }
    }, [])

    /* ── Keyboard (only while a detail is open) ──────────────────────── */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!physics.current.expanded || e.repeat) return
            if (e.key === 'Escape') close()
            else if (e.key === 'ArrowRight') next()
            else if (e.key === 'ArrowLeft') prev()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [close, next, prev])

    /* ── The rAF physics loop ────────────────────────────────────────── */
    useEffect(() => {
        if (!built || !dims) return
        const { cells, tile } = built
        const { vw, vh, isMobile } = dims
        const p = physics.current
        const introVals = intro.current
        const tileW = tile.width
        const tileH = tile.height

        /* Reset world framing for this build. */
        p.panOff.x = vw / 2
        p.panOff.y = vh / 2
        p.panTarget.x = vw / 2
        p.panTarget.y = vh / 2
        p.voidWorld.x = 0
        p.voidWorld.y = 0
        p.rowOffsets = new Array(tile.rows).fill(0)
        p.pinned = false

        /* Seed featured with the cell nearest world centre. */
        let best = 0
        let bestD = Infinity
        for (let i = 0; i < cells.length; i++) {
            const d = Math.hypot(cells[i].baseX, cells[i].baseY)
            if (d < bestD) {
                bestD = d
                best = i
            }
        }
        p.featuredIdx = best
        setFeaturedIdx(best)

        let raf = 0
        const tick = () => {
            const isExpanded = p.expanded
            const hovering = p.hovering

            /* A. per-row drift (frozen while a detail is open) */
            if (!isExpanded) {
                for (let r = 0; r < p.rowOffsets.length; r++) {
                    p.rowOffsets[r] += rowDriftSpeed(r)
                }
            }

            /* B. pan lerp toward target */
            p.panOff.x += (p.panTarget.x - p.panOff.x) * PAN_LERP
            p.panOff.y += (p.panTarget.y - p.panOff.y) * PAN_LERP

            /* C. void position (world coords) */
            if (isExpanded) {
                if (!p.pinned) {
                    const ddx = p.panTarget.x - p.panOff.x
                    const ddy = p.panTarget.y - p.panOff.y
                    if (Math.abs(ddx) < 0.5 && Math.abs(ddy) < 0.5) p.pinned = true
                }
                if (p.pinned) {
                    p.voidWorld.x = vw / 2 - p.panOff.x
                    p.voidWorld.y = vh / 2 - p.panOff.y
                }
            } else if (p.mouse.has) {
                const tx = p.mouse.x - p.panOff.x
                const ty = p.mouse.y - p.panOff.y
                p.voidWorld.x += (tx - p.voidWorld.x) * VOID_LERP
                p.voidWorld.y += (ty - p.voidWorld.y) * VOID_LERP
            }

            /* D. target radius: idle < hover < expanded, scaled by intro emerge */
            let baseR: number
            if (isExpanded) baseR = isMobile ? VOID_RADIUS_EXPANDED_MOBILE : VOID_RADIUS_EXPANDED_DESKTOP
            else if (hovering) baseR = isMobile ? VOID_RADIUS_HOVER_MOBILE : VOID_RADIUS_HOVER_DESKTOP
            else baseR = isMobile ? VOID_RADIUS_IDLE_MOBILE : VOID_RADIUS_IDLE_DESKTOP
            p.targetRadius = baseR * introVals.voidEmerge
            p.voidRadius += (p.targetRadius - p.voidRadius) * RADIUS_LERP

            const R = p.voidRadius
            const SOFT = R * SOFT_PUSH_RATIO
            const vX = p.voidWorld.x
            const vY = p.voidWorld.y

            const entrance = introVals.entrance
            const halfDiag = Math.hypot(vw, vh) / 2
            const revealActive = entrance < 1
            const cullMargin = 240

            let nearestIdx = p.featuredIdx
            let nearestDist = Infinity

            for (let i = 0; i < cells.length; i++) {
                const cell = cells[i]
                const el = itemEls.current[i]
                if (!el) continue

                const driftedX = cell.baseX + (p.rowOffsets[cell.rowIndex] ?? 0)
                const baseScreenX0 = driftedX + p.panOff.x
                const baseScreenY0 = cell.baseY + p.panOff.y
                const kX = Math.round((vw / 2 - baseScreenX0) / tileW)
                const kY = Math.round((vh / 2 - baseScreenY0) / tileH)
                const effectiveWorldX = driftedX + kX * tileW
                const effectiveWorldY = cell.baseY + kY * tileH

                const dx = effectiveWorldX - vX
                const dy = effectiveWorldY - vY
                const dist = Math.hypot(dx, dy)

                if (dist < nearestDist) {
                    nearestDist = dist
                    nearestIdx = i
                }

                const baseScreenX = effectiveWorldX + p.panOff.x
                const baseScreenY = effectiveWorldY + p.panOff.y
                if (baseScreenX + cell.width < -cullMargin) continue
                if (baseScreenX > vw + cullMargin) continue
                if (baseScreenY + cell.height < -cullMargin) continue
                if (baseScreenY > vh + cullMargin) continue

                let finalWorldX = effectiveWorldX
                let finalWorldY = effectiveWorldY
                let s = cell.scale

                if (dist < SOFT) {
                    const t = 1 - dist / SOFT
                    const push = R * t * t
                    let dirX: number
                    let dirY: number
                    if (dist < 0.5) {
                        dirX = Math.cos(cell.angle)
                        dirY = Math.sin(cell.angle)
                    } else {
                        dirX = dx / dist
                        dirY = dy / dist
                    }
                    finalWorldX = effectiveWorldX + dirX * push
                    finalWorldY = effectiveWorldY + dirY * push
                    if (dist > R * 0.55 && dist < R * 1.05) {
                        const ringT =
                            smoothstep(R * 0.55, R * 0.8, dist) * (1 - smoothstep(R * 0.85, R * 1.05, dist))
                        s *= 1 + 0.06 * ringT
                    }
                }

                let alpha = 1
                let introOffsetX = 0
                let introOffsetY = 0
                if (revealActive) {
                    const distFromCenter = Math.hypot(cell.baseX, cell.baseY)
                    const cellEntrance = entrance * 1.0 - (distFromCenter / halfDiag) * 0.4
                    const entranceAlpha = Math.max(0, Math.min(1, cellEntrance * 1.5))
                    alpha = entranceAlpha
                    s *= 0.3 + 0.7 * entranceAlpha
                    if (distFromCenter > 1) {
                        const dirX = cell.baseX / distFromCenter
                        const dirY = cell.baseY / distFromCenter
                        const flyMag = (1 - entranceAlpha) * 420
                        introOffsetX = dirX * flyMag
                        introOffsetY = dirY * flyMag
                    }
                }

                const screenX = finalWorldX + introOffsetX + p.panOff.x - cell.width / 2
                const screenY = finalWorldY + introOffsetY + p.panOff.y - cell.height / 2
                el.style.transform =
                    `translate3d(${screenX.toFixed(2)}px, ${screenY.toFixed(2)}px, 0) ` +
                    `rotate(${cell.rotation.toFixed(2)}deg) scale(${s.toFixed(3)})`
                el.style.opacity = alpha < 0.999 ? alpha.toFixed(3) : '1'
            }

            /* F. featured index update (only on real change, only when closed) */
            if (nearestIdx !== p.featuredIdx && nearestDist < R * 0.9 && !isExpanded) {
                p.featuredIdx = nearestIdx
                setFeaturedIdx(nearestIdx)
            }

            /* G. position the featured anchor */
            const anchor = anchorRef.current
            if (anchor) {
                let sx: number
                let sy: number
                if (isExpanded && p.pinned) {
                    sx = vw / 2
                    sy = vh / 2
                } else {
                    sx = vX + p.panOff.x
                    sy = vY + p.panOff.y
                }
                const fEm = introVals.featuredEmerge
                anchor.style.transform = `translate3d(${sx.toFixed(2)}px, ${sy.toFixed(2)}px, 0) scale(${fEm.toFixed(3)})`
                anchor.style.opacity = fEm < 0.999 ? fEm.toFixed(3) : '1'
            }

            raf = requestAnimationFrame(tick)
        }

        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [built, dims])

    /* ── Cinematic intro (runs once, snaps to final on teardown) ─────── */
    useEffect(() => {
        if (!built || introStarted.current) return
        introStarted.current = true

        const introVals = intro.current
        introVals.entrance = 0
        introVals.voidEmerge = 0
        introVals.featuredEmerge = 0
        physics.current.voidRadius = 0
        physics.current.targetRadius = 0

        const chars = titleRef.current ? titleRef.current.querySelectorAll('[data-char]') : []
        gsap.set(eyebrowRef.current, { opacity: 0, y: 18, filter: 'blur(8px)' })
        gsap.set(chars, { opacity: 0, y: 22, scale: 0.8, filter: 'blur(10px)' })
        gsap.set(subRef.current, { opacity: 0, y: 18, filter: 'blur(8px)' })
        gsap.set(hintRef.current, { opacity: 0, y: 18, filter: 'blur(8px)' })

        const tl = gsap.timeline({ delay: 0.1 })
        tl.to(introVals, { entrance: 1, duration: 1.7, ease: 'power2.inOut' }, 0)
        tl.to(introVals, { voidEmerge: 1, duration: 1.1, ease: 'power2.out' }, 0.2)
        tl.to(introVals, { featuredEmerge: 1, duration: 0.8, ease: 'back.out(1.4)' }, 0.7)
        tl.to(eyebrowRef.current, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, ease: 'power3.out' }, 1.1)
        tl.to(
            chars,
            { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.9, ease: 'power4.out', stagger: 0.04 },
            1.25
        )
        tl.to(subRef.current, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, ease: 'power3.out' }, 1.6)
        tl.to(hintRef.current, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, ease: 'power3.out' }, 1.7)

        return () => {
            tl.kill()
            introVals.entrance = 1
            introVals.voidEmerge = 1
            introVals.featuredEmerge = 1
            if (eyebrowRef.current) gsap.set(eyebrowRef.current, { opacity: 1, y: 0, filter: 'blur(0px)' })
            if (titleRef.current)
                gsap.set(titleRef.current.querySelectorAll('[data-char]'), {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: 'blur(0px)'
                })
            if (subRef.current) gsap.set(subRef.current, { opacity: 1, y: 0, filter: 'blur(0px)' })
            if (hintRef.current) gsap.set(hintRef.current, { opacity: 1, y: 0, filter: 'blur(0px)' })
        }
    }, [built])

    return (
        <section
            ref={sectionRef}
            aria-label="Moments — interactive photography wall"
            className="relative isolate h-screen w-full select-none overflow-hidden bg-background"
        >
            <div aria-hidden className="absolute inset-0">
                {built && dims && <Wall cells={built.cells} photos={photos} itemsRef={itemEls} />}
            </div>

            {/* Hero gradient backdrop (below the dim overlay) */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[14]"
                style={{
                    background:
                        'radial-gradient(ellipse 70% 55% at 0% 100%, rgb(var(--background-rgb) / 1) 0%, rgb(var(--background-rgb) / 0.85) 22%, rgb(var(--background-rgb) / 0.55) 40%, rgb(var(--background-rgb) / 0.25) 58%, transparent 78%)'
                }}
            />

            {/* Dim overlay (fades in while expanded, backdrop click closes) */}
            <div
                aria-hidden
                onClick={close}
                className="absolute inset-0 z-20 transition-opacity duration-300"
                style={{
                    background: 'rgb(var(--background-rgb) / 0.55)',
                    opacity: expanded ? 1 : 0,
                    pointerEvents: expanded ? 'auto' : 'none'
                }}
            />

            {/* Featured anchor (positioned every frame by the tick) */}
            <div
                ref={anchorRef}
                className="pointer-events-none absolute left-0 top-0 z-30 will-change-transform"
                style={{ opacity: 0 }}
            >
                {built && dims && (
                    <FeaturedCard
                        photo={currentPhoto}
                        expanded={expanded}
                        isMobile={dims.isMobile}
                        liked={liked.has(currentPhoto.slug)}
                        saved={saved.has(currentPhoto.slug)}
                        onOpen={open}
                        onClose={close}
                        onNext={next}
                        onPrev={prev}
                        onToggleLike={toggleLike}
                        onToggleSave={toggleSave}
                        onShare={share}
                        onDownload={download}
                        onOrder={order}
                        onInquire={inquire}
                    />
                )}
            </div>

            {/* Hero copy (above the featured so it is never covered) */}
            <div
                ref={heroRef}
                className={cn(
                    'pointer-events-none absolute bottom-7 left-7 z-[35] max-w-md',
                    expanded && dims?.isMobile && 'hidden'
                )}
                style={{ color: 'var(--foreground)' }}
            >
                <div
                    ref={eyebrowRef}
                    className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]"
                    style={{ color: 'rgb(var(--foreground-rgb) / 0.65)', opacity: 0 }}
                >
                    <span className="inline-block h-px w-8" style={{ background: 'rgb(var(--foreground-rgb) / 0.45)' }} />
                    A photography wall
                </div>
                <h1
                    ref={titleRef}
                    className="mt-3"
                    style={{
                        fontFamily: 'var(--font-playfair), Georgia, serif',
                        fontSize: 'clamp(2.25rem, 4vw, 3.75rem)',
                        lineHeight: 0.95,
                        letterSpacing: '-0.02em'
                    }}
                >
                    {'Moments'.split('').map((ch, i) => (
                        <span
                            key={i}
                            data-char
                            className="inline-block whitespace-pre will-change-transform"
                            style={{ opacity: 0 }}
                        >
                            {ch}
                        </span>
                    ))}
                </h1>
                <p
                    ref={subRef}
                    className="mt-3 max-w-xs text-[13px] leading-relaxed max-md:hidden"
                    style={{ color: 'rgb(var(--foreground-rgb) / 0.6)', opacity: 0 }}
                >
                    The wall parts wherever your cursor goes. Click to centre the moment.
                </p>
            </div>

            {/* Interaction hint (fades on first move) */}
            <div
                ref={hintRef}
                className="pointer-events-none absolute right-7 top-20 z-40 text-[11px] uppercase tracking-[0.22em]"
                style={{ color: 'rgb(var(--foreground-rgb) / 0.55)', opacity: 0 }}
            >
                Move · Click to open
            </div>
        </section>
    )
}
